import React, {useContext, useEffect, useRef, useState, useReducer} from 'react';
import {
    Box,
    Button,
    Flex,
    Stack,
    Step,
    StepIndicator,
    StepSeparator,
    StepStatus,
    Stepper,
    StepTitle,
    FormControl, FormLabel, Input,
    useSteps, StepIcon, StepNumber, useToast, VStack, Image, Heading, Text, Badge,
    Skeleton, SkeletonText, Alert, AlertIcon,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    InputGroup, InputLeftAddon
} from "@chakra-ui/react";
import { useCustomerAuth } from "../Context/AuthContext";
import { saveCustomerPhone } from "../Services/aws/UserAuthenticationApiGateway";
import PaymentPage from '../Components/LaundryPickup/PaymentPage';
import UnifiedServicePage from '../Components/LaundryPickup/UnifiedServicePage';
import SchedulePage from '../Components/LaundryPickup/SchedulePage';
import UnifiedReviewPage from '../Components/LaundryPickup/UnifiedReviewPage';
import MembershipOfferBanner from '../Components/LaundryPickup/MembershipOfferBanner';
import cartReducer, { initialCartState } from '../Components/LaundryPickup/cartReducer';
import { buildOrderPayload, getCartSubtotal, getAddonsTotal, getBilledWeight, derivePricingType } from '../Components/LaundryPickup/cartUtils';
import AddonPicker from '../Components/LaundryPickup/AddonPicker';
import {useNavigate, useSearchParams} from "react-router-dom";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from "axios";
import PlacesSuggestInput from "../Components/SiteLanding/PlacesSuggestInput";
import {formatISO, parseISO} from "date-fns";
import { toZonedTime, format } from 'date-fns-tz';
import {addDays} from 'date-fns';
import {LaundryContext} from "../Components/Contexts/LaundryContext";
import { MULTI_PROPERTY_ENABLED } from "../featureFlags";
import { track } from "../utils/analytics";
import { dryCleanReturnDate } from "../utils/dryCleanSchedule";

// Format a raw phone into (AAA) BBB-CCCC for display; strips a leading country
// code and non-digits. Falls back to the original when it isn't 10 digits.
const formatPhone = (raw) => {
    if (!raw) return '';
    let digits = String(raw).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    return raw;
};
// tel: href with a leading + and country code so it dials on mobile.
const telHref = (raw) => {
    if (!raw) return '';
    let digits = String(raw).replace(/\D/g, '');
    if (digits.length === 10) digits = `1${digits}`;
    return `tel:+${digits}`;
};

export default function LaundryPickupPage({laundryId,customerId,customerPaymentId,setCustomerPaymentId, laundryTimeZone, setLaundryTimeZone, specialInstructions, setSpecialInstructions}) {
    // Fixed 5-step stepper (Order Type + Services + Schedule + Payment + Review)
    // Effortless-ordering flow: lead with services, ask for address only after
    // the customer has built their cart. Serviceability is enforced at the
    // Address step (can't advance unless validated) and re-checked at place-order.
    const steps = [
        { title: 'Services' },
        { title: 'Address' },
        { title: 'Schedule' },
        { title: 'Payment' },
        { title: 'Review' },
    ];

    // Order type. There is no separate Order Type step anymore — the plan
    // (one-time / recurring / subscribe & save) is chosen on the Schedule page.
    // Default to one-time so the customer lands straight on Services.
    const [orderType, setOrderType] = useState('one-time'); // 'one-time' | 'frequency' | 'subscribe-save'

    // Pre-selected order type from landing page navigation
    const [preSelectedType] = useState(() => {
        const saved = localStorage.getItem('selectedOrderType');
        if (saved) {
            localStorage.removeItem('selectedOrderType');
            return saved;
        }
        return null;
    });
    // True once the customer picks a plan, or a reorder forces one-time.
    // Stops the weekly default from coming back after they opt out.
    const planChosenRef = useRef(false);
    const repeatSeededRef = useRef(false);

    // Cart state via useReducer (Task 5.3)
    const [cart, dispatch] = useReducer(cartReducer, initialCartState);

    const { laundryData } = useContext(LaundryContext);
    const {activeStep, setActiveStep} = useSteps({index: 0});
    const [isPaymentStepValid,setIsPaymentStepValid] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useToast();
    const authToken = localStorage.getItem('idToken');
    // Commercial hierarchy: a parent_admin placing an order FOR a child location
    // arrives here with ?forCustomerId=<child>. When present and different from
    // the logged-in customer, we send the order on behalf of that child (backend
    // authorizes the parent->child link and owns the order to the child).
    const forCustomerId = searchParams.get('forCustomerId') || null;
    const isPlacingForChild = !!forCustomerId && forCustomerId !== customerId;
    const [isAddressValidating,setIsAddressValidating] = useState(false);

    // State Management for scheduling
    const [pickupDate, setPickupDate] = useState('');
    const [pickupTime, setPickupTime] = useState('');
    const [dropoffDate, setDropoffDate] = useState('');
    const [dryCleanDropoffDate, setDryCleanDropoffDate] = useState('');
    const [dropoffTime, setDropoffTime] = useState('');
    const [frequency, setFrequency] = useState(null);
    const [promoCode, setPromoCode] = useState('');
    const [giftCardCode, setGiftCardCode] = useState('');
    const [laundryBags, setLaundryBags] = useState(1);
    const [saveSpecialInstructions, setSaveSpecialInstructions] = useState(false);
    const [frequencyPromotions, setFrequencyPromotions] = useState([]);
    const [promoDescriptionMessage, setPromoDescriptionMessage] = useState('');

    // State management for the payment page
    const [existingPaymentMethods, setExistingPaymentMethods] = useState([]);
    const [payByInvoice, setPayByInvoice] = useState(false);
    const [isCommercialCustomer, setIsCommercialCustomer] = useState(false);
    const [hasMembership, setHasMembership] = useState(false);
    // Multi-address ("Airbnb cleaner") account: when true, the address step lets
    // the customer add multiple properties, each weighed + billed separately.
    const [isMultiProperty, setIsMultiProperty] = useState(false);
    // Extra properties beyond the primary address (each: {address, unitName,
    // doorNumber, addressInstructions}). The primary address stays in `address`.
    const [extraProperties, setExtraProperties] = useState([]);

    // State management for the review order page
    const [orderProcessing, setOrderProcessing] = useState(false);
    const [tip, setTip] = useState({
        tipOption: '5',
        tipType: 'percentage',
        tipAmount: '0.00',
        tipPercentage: 5,
        tipReceivedId: '',
        tipMethod: 'Card',
        customTip: '',
    });

    // State variables for address validation
    const [address, setAddress] = useState(localStorage.getItem('customerAddress') || '');
    const [addressInstructions,setAddressInstructions] = useState('');
    const [doorNumber, setDoorNumber] = useState('');
    const [isAddressValidated, setIsAddressValidated] = useState(!!localStorage.getItem('customerAddress'));
    // Persistent "outside delivery range" notice (serviceable zip but beyond the
    // max distance). Rendered as a dismissible box with a clickable phone link —
    // a toast can't hold a tel: link and disappears too fast to read.
    const [tooFar, setTooFar] = useState(null); // { contactPhone } | null
    // Service-zone pickup days for the validated address. null = no zone
    // restriction (use the tenant's global operating days). An array like
    // ['Monday','Wednesday'] limits pickup to that zone's days.
    const [allowedPickupDays, setAllowedPickupDays] = useState(null);
    // Zone-specific DELIVERY (return) days for the validated address. null =
    // no zone-specific delivery days -> the return date uses the tenant's
    // global delivery schedule (deliveryTimeSlots). An array like
    // ['Tuesday','Friday'] restricts the return to that zone's delivery days.
    const [allowedDeliveryDays, setAllowedDeliveryDays] = useState(null);

    // State variables for laundry info
    const [laundryServices, setLaundryServices] = useState([]);
    // Phase 2: tenant master opt-in for minimum billable weight
    const [minWeightEnabled, setMinWeightEnabled] = useState(false);
    // Phase 2c: add-ons
    const [laundryAddons, setLaundryAddons] = useState([]);
    const [addonsEnabled, setAddonsEnabled] = useState(false);
    const [selectedAddons, setSelectedAddons] = useState({}); // addonId -> {addonId, addonName, pricingBasis, unitPrice, quantity}
    const [saveAddonPrefs, setSaveAddonPrefs] = useState(false); // Phase 2d: save selection as default
    const [addonPrefsLoaded, setAddonPrefsLoaded] = useState(false);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [servicesLoaded, setServicesLoaded] = useState(false);
    const [deliveryTimeSlots, setDeliveryTimeSlots] = useState([]);
    const [deliveryTimeInterval, setDeliveryTimeInterval] = useState(0);
    const [laundryFrequency, setLaundryFrequency] = useState([]);
    const [stripePromise, setStripePromise] = useState(null);
    const [pickupService, setPickupService] = useState("");
    const [dropoffService, setDropoffService] = useState("");
    const [uberExists, setUberExists] = useState(false);
    const [uberEnv, setUberEnv] = useState("");
    const [laundryAddress, setLaundryAddress] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [uberPickupFrequency, setUberPickupFrequency] = useState(false);
    const [uberDropoffFrequency, setUberDropoffFrequency] = useState(false);
    const [doordashExists, setDoordashExists] = useState(false);
    const [missedPickupFee, setMissedPickupFee] = useState(0);
    const [courierHours, setCourierHours] = useState(null);
    const [doordashPickupFrequency, setDoordashPickupFrequency] = useState(false);
    const [doordashDropoffFrequency, setDoordashDropoffFrequency] = useState(false);

    // Function to format dates in the laundry's time zone
    const getDateInTimeZone = (date, timeZone) => {
        const zonedDate = toZonedTime(date, timeZone);
        return format(zonedDate, 'yyyy-MM-dd', { timeZone });
    };

    // Check if customer is a commercial account and auto-set payByInvoice
    useEffect(() => {
        const checkCommercialStatus = async () => {
            if (!customerId || !laundryId || !authToken) return;
            try {
                const response = await axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/get-orders-info`, {
                    params: { operation: 'getCustomerProfile', customerId, laundryId },
                    headers: { 'x-api-key': authToken },
                });
                const profile = response.data?.body;
                if (profile?.isCommercial) {
                    setIsCommercialCustomer(true);
                    setPayByInvoice(true);
                    setIsPaymentStepValid(true);
                }
                if (MULTI_PROPERTY_ENABLED && profile?.isMultiProperty) {
                    setIsMultiProperty(true);
                    // Pre-fill previously-saved properties so the cleaner doesn't
                    // re-type them. Property 1 is the primary pickup address
                    // (seeded separately); the rest become editable extra
                    // properties. They can add/edit/remove from here.
                    const saved = Array.isArray(profile.savedProperties) ? profile.savedProperties : [];
                    if (saved.length > 1) {
                        setExtraProperties(saved.slice(1).map((p) => ({
                            _key: `saved_${p.addressId}`,
                            addressId: p.addressId,
                            address: p.address || '',
                            unitName: p.label || '',
                            doorNumber: p.doorNumber || '',
                            addressInstructions: p.addressInstructions || '',
                        })));
                    }
                }
            } catch (err) {
                // If the profile check fails, customer proceeds as non-commercial
                console.debug('Commercial status check skipped:', err.message);
            }
        };
        checkCommercialStatus();
    }, [customerId, laundryId, authToken]);

    // Commercial group context: members of an active group are invoice-billed;
    // members of a group that isn't approved (or a paused location) can't order,
    // so show the status instead of the order form. When a parent orders for a
    // location, prefill that location's address.
    const [groupCtx, setGroupCtx] = useState(null); // { groupName, childLabel, blocked, message, isParent, locations }
    // An owner with locations picks one before booking; this marks "for my own account".
    const [ownAccountChosen, setOwnAccountChosen] = useState(false);
    const setForCustomer = (childId) => {
        const next = new URLSearchParams(searchParams);
        if (childId) {
            next.set('forCustomerId', childId);
        } else {
            next.delete('forCustomerId');
            setAddress(localStorage.getItem('customerAddress') || '');
            setIsAddressValidated(false);
        }
        setSearchParams(next, { replace: true });
        setActiveStep(0);
    };
    const chooseLocation = (childId) => {
        setOwnAccountChosen(!childId);
        setForCustomer(childId);
    };
    const reopenLocationPicker = () => {
        setOwnAccountChosen(false);
        setForCustomer(null);
    };
    useEffect(() => {
        if (!customerId || !laundryId || !authToken) return;
        let cancelled = false;
        const statusMessage = (status) => ({
            draft: 'Your commercial account hasn\'t been submitted for approval yet. Finish setup on the Commercial page to start ordering.',
            pending_approval: 'Your commercial account is waiting for the laundry\'s approval. Ordering unlocks once it\'s approved.',
            rejected: 'Your commercial account wasn\'t approved. See the Commercial page for details.',
            suspended: 'Your commercial account is paused by the laundry. Please contact them to resume ordering.',
        }[status] || 'Your commercial account can\'t place orders right now.');

        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/commercial-group/mine`, {
            params: { customerId, laundryId },
            headers: { 'x-api-key': authToken },
        }).then((res) => {
            if (cancelled) return;
            const b = res.data || {};
            if (!b.inGroup) {
                if (isPlacingForChild) {
                    setGroupCtx({ blocked: true, message: 'This location isn\'t part of your commercial account.' });
                }
                return;
            }
            const g = b.group || {};
            let childLabel = null;
            let blocked = false;
            let message = '';
            if (isPlacingForChild) {
                const child = b.role === 'parent_admin'
                    ? (g.members || []).find((m) => m.customerId === forCustomerId)
                    : null;
                if (!child) {
                    blocked = true;
                    message = 'This location isn\'t part of your commercial account.';
                } else {
                    childLabel = child.locationLabel || child.name || 'Location';
                    if (child.isSuspended) {
                        blocked = true;
                        message = `${childLabel} is paused by the laundry. Please contact them to resume ordering for this location.`;
                    } else if (child.address) {
                        setAddress(child.address);
                        setIsAddressValidated(false);
                    }
                }
            } else if (b.memberSuspended) {
                blocked = true;
                message = 'Ordering for this location is paused by the laundry. Please contact them to resume.';
            }
            if (!blocked && g.status !== 'active') {
                blocked = true;
                message = statusMessage(g.status);
            }
            if (!blocked) {
                setIsCommercialCustomer(true);
                setPayByInvoice(true);
                setIsPaymentStepValid(true);
            }
            const locations = b.role === 'parent_admin'
                ? (g.members || []).filter((m) => m.role !== 'parent_admin')
                : [];
            setGroupCtx({ groupName: g.groupName, childLabel, blocked, message, isParent: b.role === 'parent_admin', locations });
        }).catch(() => {
            if (!cancelled && isPlacingForChild) {
                setGroupCtx({ blocked: true, message: 'Couldn\'t load your commercial account. Please try again.' });
            }
        });
        return () => { cancelled = true; };
    }, [customerId, laundryId, authToken, forCustomerId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch laundry info when the page loads.
    // Perf: stale-while-revalidate. The service catalog rarely changes, so we
    // render instantly from a per-tenant localStorage cache (no blank / no
    // skeleton wait for returning customers), then re-fetch in the background
    // and silently update if anything changed. A short TTL bounds staleness so
    // a price change is never shown stale for long.
    useEffect(() => {
        const CACHE_PREFIX = 'slb_laundryInfo_';
        const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min: show cache instantly, still revalidate every load

        const applyLaundryInfo = (data) => {
            const mwEnabled = data.minWeightEnabled === true;
            setMinWeightEnabled(mwEnabled);
            setLaundryServices(
                (data.laundryServices || []).map((s) => ({ ...s, minWeightEnabled: mwEnabled }))
            );
            setAddonsEnabled(data.addonsEnabled === true);
            setLaundryAddons(data.laundryAddons || []);
            setDeliveryTimeSlots(data.deliveryTimeSlots);
            setLaundryTimeZone(data.laundryTimeZone);
            setDeliveryTimeInterval(parseInt(data.deliveryTimeInterval, 10));
            setLaundryFrequency(data.frequencyInterval);
            setFrequencyPromotions(data.frequencyPromotions || []);
            setLaundryAddress(data.laundryAddress || '');
            setContactPhone(data.contactPhone || '');
            setUberEnv(data.uberEnv || '');
            setUberExists(data?.uberCredentialsExist === true);
            setDoordashExists(data?.doordashConnected === true);
            setMissedPickupFee(Number(data?.missedPickupFee) || 0);
            setCourierHours(Array.isArray(data?.courierHours) ? data.courierHours : null);
            setServiceCategories(data.serviceCategories || []);
            setServicesLoaded(true);
            if (data.stripePublicKey) {
                setStripePromise(loadStripe(data.stripePublicKey));
            } else if (laundryData?.stripePublicKey) {
                setStripePromise(loadStripe(laundryData.stripePublicKey));
            }
        };

        const fetchLaundryInfo = async () => {
            if (!authToken) {
                toast({
                    title: "Please Authenticate first",
                    description: "User not Logged In!",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                navigate(`/${laundryId}/login`);
                return;
            }

            // 1) Instant render from a fresh-enough cache (skips the 1s blank).
            const cacheKey = `${CACHE_PREFIX}${laundryId}`;
            let cachedPayload = null;
            try {
                const raw = localStorage.getItem(cacheKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    cachedPayload = parsed?.data || null;
                    if (cachedPayload && parsed.ts && (Date.now() - parsed.ts) < CACHE_TTL_MS) {
                        applyLaundryInfo(cachedPayload);
                    }
                }
            } catch (e) {
                cachedPayload = null; // corrupt cache — ignore, fall through to fetch
            }

            // 2) Always revalidate in the background; update only if changed.
            try {
                const response = await axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/laundry/get-info`, {
                    params: { operation: 'getLaundryInfo', laundryId: laundryId, isCustomer: true },
                    headers: { 'x-api-key': authToken },
                });
                if (response.data.status === 'success') {
                    const fresh = response.data;
                    const changed = JSON.stringify(fresh) !== JSON.stringify(cachedPayload);
                    if (changed || !servicesLoaded) {
                        applyLaundryInfo(fresh);
                    }
                    try {
                        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: fresh }));
                    } catch (e) { /* storage full / disabled — cache is best-effort */ }
                } else if (!cachedPayload) {
                    // Only surface an error when we have nothing to show.
                    toast({ title: "Error fetching laundry info", status: "error", duration: 3000, isClosable: true });
                }
            } catch (error) {
                if (!cachedPayload) {
                    toast({ title: "Error", description: error.message, status: "error", duration: 3000, isClosable: true });
                }
                // If we already rendered from cache, a background failure is silent.
            }
        };
        fetchLaundryInfo();

    }, [toast, navigate, laundryId, authToken, setLaundryTimeZone]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize the pickup date after laundryTimeZone is available. We do NOT
    // seed a dropoff date here: the return must always be derived from pickup +
    // the cart's turnaround, rolled to the next delivery day. Seeding a raw
    // "today + 2" here created a return disconnected from pickup/turnaround
    // (e.g. it could sit days after the true earliest and never get corrected,
    // because the anchor logic only pulls a return EARLIER, never later). The
    // min-anchored effects (here and in SchedulePage) set dropoff correctly.
    useEffect(() => {
        if (laundryTimeZone && !pickupDate) {
            const today = new Date();
            const initialPickupDate = getDateInTimeZone(addDays(today, 1), laundryTimeZone);
            setPickupDate(initialPickupDate);
        }
    }, [laundryTimeZone, pickupDate]);

    // Track cart-started for abandoned cart SMS recovery
    const furthestStepTracked = useRef(-1);
    useEffect(() => {
        if (activeStep <= furthestStepTracked.current) return;
        furthestStepTracked.current = activeStep;
        if (activeStep === 0) track('begin_checkout', { currency: 'USD' });
        track('checkout_step', { step_number: activeStep + 1, step_name: steps[activeStep]?.title });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    useEffect(() => {
        if (servicesLoaded && customerId && laundryId) {
            axios.post(`${process.env.REACT_APP_AWS_API_URL}/api/customer/cart-started`, {
                customerId,
                laundryId,
            }).catch(() => {}); // Silent — never block the order flow
        }
    }, [servicesLoaded, customerId, laundryId]);

    // Phase 2d: prefill the add-on picker from the customer's saved defaults so
    // they don't have to re-select their usual extras. Runs once the add-on
    // catalog is available; matches saved ids against the live catalog so prices
    // and basis are always current.
    useEffect(() => {
        if (addonPrefsLoaded) return;
        if (!addonsEnabled || !customerId || !laundryId || laundryAddons.length === 0) return;
        setAddonPrefsLoaded(true);
        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/addon-preferences`, {
            params: { customerId, laundryId },
            headers: { 'x-api-key': authToken },
        }).then((res) => {
            const saved = res.data?.addons || [];
            if (saved.length === 0) return;
            const next = {};
            saved.forEach((s) => {
                const id = s.addonId ?? s.addon_id;
                const cat = laundryAddons.find((a) => a.addonId === id);
                if (!cat) return; // add-on no longer offered — skip
                next[id] = {
                    addonId: cat.addonId,
                    addonName: cat.addonName,
                    pricingBasis: cat.pricingBasis,
                    unitPrice: cat.unitPrice,
                    quantity: cat.pricingBasis === 'per_item' ? (s.quantity || 1) : null,
                    bagPrices: cat.bagPrices || [],
                };
            });
            if (Object.keys(next).length > 0) setSelectedAddons(next);
        }).catch(() => {}); // Silent — never block the order flow
    }, [addonsEnabled, customerId, laundryId, laundryAddons, addonPrefsLoaded, authToken]);

    // Task 5.5: Single-service auto-add logic (no auto-skip)
    // If only 1 service exists, auto-add it to cart but let customer stay on step 1
    // so they can optionally enter weight before continuing
    useEffect(() => {
        if (servicesLoaded && laundryServices.length === 1 && cart.items.length === 0 && orderType && activeStep === 0) {
            const singleService = laundryServices[0];
            const isWeight = singleService.inputWeight === true || singleService.inputWeight === 'true';
            dispatch({
                type: 'ADD_ITEM',
                payload: {
                    serviceId: singleService.serviceId || singleService.serviceName,
                    serviceName: singleService.serviceName,
                    categoryId: singleService.categoryId || 'uncategorized',
                    categoryName: singleService.categoryName || 'Uncategorized',
                    isDryClean: !!singleService.isDryClean,
                    price: parseFloat(singleService.price),
                    inputWeight: isWeight,
                    quantity: isWeight ? 1 : 1,
                },
            });
            // Don't auto-advance — let customer see the service, enter weight, and click Continue
        }
    }, [servicesLoaded, laundryServices, cart.items.length, orderType, activeStep]);

    // The current cart's pricing shape drives which plans are offered on the
    // Schedule page. Subscribe & Save applies to bag-only (all per-item) carts.
    const cartPricingType = derivePricingType(cart.items); // 'per_pound'|'per_item'|'per_bag'|'mixed'
    const cartIsBagOnly = cartPricingType === 'per_item' || cartPricingType === 'per_bag';
    // Slowest service in the cart governs the earliest possible return: an order
    // can't come back before its slowest item is done. Take the MAX turnaround
    // across cart items that have an explicitly configured turnaround. A value
    // of 0 IS meaningful (same-day) and is kept. When NO item has a configured
    // turnaround, this is null → SchedulePage keeps the normal next-day minimum.
    const laundryCartItems = (cart.items || []).filter((it) => !it.isDryClean);
    const dryCleanCartItems = (cart.items || []).filter((it) => it.isDryClean);
    const dryCleanOnly = dryCleanCartItems.length > 0 && laundryCartItems.length === 0;
    const mixedDryClean = dryCleanCartItems.length > 0 && laundryCartItems.length > 0;
    // Wash-and-fold return ignores dry-cleaning pieces. Those have their own date.
    const maxTurnaroundHours = laundryCartItems.reduce((max, it) => {
        const t = Number(it.turnaroundHours);
        if (!Number.isFinite(t) || t < 0) return max;      // unset/invalid → ignore
        return (max == null || t > max) ? t : max;
    }, null);
    const dryCleanMinDate = (() => {
        if (!dryCleanCartItems.length || !pickupDate) return "";
        const shopDays = laundryData?.dryCleanTurnaroundDays;
        const fromPieces = dryCleanCartItems.reduce((max, it) => {
            const hours = Number(it.turnaroundHours);
            if (!Number.isFinite(hours) || hours < 0) return max;
            const days = Math.ceil(hours / 24);
            return max == null || days > max ? days : max;
        }, null);
        const turnaroundDays = shopDays != null ? shopDays : (fromPieces != null ? fromPieces : 4);
        const globalDeliveryDays = (deliveryTimeSlots || []).map((s) => s && s.day).filter(Boolean).map(String);
        let deliveryDays = globalDeliveryDays;
        if (Array.isArray(allowedDeliveryDays) && allowedDeliveryDays.length > 0) {
            const intersected = allowedDeliveryDays.map(String).filter((d) => globalDeliveryDays.length === 0 || globalDeliveryDays.includes(d));
            deliveryDays = intersected.length > 0 ? intersected : globalDeliveryDays;
        }
        return dryCleanReturnDate({
            pickup: pickupDate,
            acceptDays: laundryData?.dryCleanAcceptDays || [],
            turnaroundDays,
            deliveryDays,
            closureDates: laundryData?.closureDates || [],
            timeSelectionOn: laundryData?.customerTimeSelectionEnabled ?? true,
        });
    })();

    // Recalculate the return (dropoff) date whenever the cart's service SPEED
    // changes (Standard / Next-Day / Same-Day). This lives in the PARENT — not
    // SchedulePage — because SchedulePage unmounts when the customer navigates
    // to another step, so a reset kept there would be lost when they come back
    // and change the service. pickupDate / dropoffDate / turnaround all persist
    // here, so the return always reflects the current service:
    //   • Standard (>=48h) -> earliest return 2 days after pickup
    //   • Next-Day (24h)   -> next day
    //   • Same-Day (0h)    -> same date as pickup
    // (SchedulePage still nudges the return forward if the same-day cutoff has
    //  passed.) A ref tracks the last turnaround so we only reset on a genuine
    //  service change, not on unrelated re-renders.
    const prevTurnaroundRef = useRef(undefined);
    useEffect(() => {
        if (!dryCleanMinDate) {
            if (dryCleanDropoffDate) setDryCleanDropoffDate("");
            return;
        }
        if (dryCleanOnly) {
            if (dropoffDate !== dryCleanMinDate) setDropoffDate(dryCleanMinDate);
            return;
        }
        if (dryCleanDropoffDate !== dryCleanMinDate) setDryCleanDropoffDate(dryCleanMinDate);
    }, [dryCleanMinDate, dryCleanOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dryCleanOnly) return;
        if (!laundryTimeZone || !pickupDate) return;
        const prev = prevTurnaroundRef.current;
        if (prev === undefined) {
            // First observation — record baseline without overriding a restored date.
            prevTurnaroundRef.current = maxTurnaroundHours;
            return;
        }
        if (prev === maxTurnaroundHours) return; // no service-speed change
        prevTurnaroundRef.current = maxTurnaroundHours;

        const days = (maxTurnaroundHours == null || !Number.isFinite(Number(maxTurnaroundHours)))
            ? 1
            : Math.max(0, Math.ceil(Number(maxTurnaroundHours) / 24));
        const raw = getDateInTimeZone(
            addDays(new Date(`${pickupDate}T00:00:00`), days),
            laundryTimeZone
        );
        // Roll the raw earliest forward past closures / non-operating weekdays
        // (e.g. Fri +48h -> Sun -> Sun/Mon holiday closed -> Tue). Mirrors the
        // backend scheduling.earliest_return_date + SchedulePage helper.
        const closureSet = new Set((laundryData?.closureDates || []).map((d) => String(d || '').trim()).filter(Boolean));
        // The RETURN date is gated by DELIVERY days. Do NOT use allowedPickupDays
        // here: zones restrict which days an area is PICKED UP, not delivered.
        // When the address maps to a zone with its OWN delivery days, use those
        // (intersected with the global delivery schedule so the day still has
        // delivery hours). Otherwise fall back to the tenant's global delivery
        // days from deliveryTimeSlots. Mirrors SchedulePage._deliveryDayNames.
        const globalDeliveryDays = new Set(
            (deliveryTimeSlots || []).map((s) => s && s.day).filter(Boolean).map(String)
        );
        let operatingDayNames;
        if (Array.isArray(allowedDeliveryDays) && allowedDeliveryDays.length > 0) {
            const zoneDays = allowedDeliveryDays.map(String);
            const intersected = zoneDays.filter((d) => globalDeliveryDays.size === 0 || globalDeliveryDays.has(d));
            // If the zone's delivery days don't overlap the global schedule at
            // all (misconfig), fall back to global rather than zero eligible days.
            operatingDayNames = new Set(intersected.length > 0 ? intersected : globalDeliveryDays);
        } else {
            operatingDayNames = globalDeliveryDays;
        }
        const dayName = (yyyyMmDd) => format(toZonedTime(new Date(`${yyyyMmDd}T00:00`), laundryTimeZone), 'EEEE', { timeZone: laundryTimeZone });
        // Date-only / notify-later tenants (customerTimeSelectionEnabled = false):
        // return = pickup + turnaround, skipping only closures — NOT rolled onto a
        // configured delivery weekday (Option A). Time-window tenants keep the
        // delivery-day gating so the return lands on an actual route day.
        const _timeSelectionOn = laundryData?.customerTimeSelectionEnabled ?? true;
        const eligible = (yyyyMmDd) => !closureSet.has(yyyyMmDd)
            && (!_timeSelectionOn || operatingDayNames.size === 0 || operatingDayNames.has(dayName(yyyyMmDd)));
        let earliest = raw;
        let walk = new Date(`${raw}T00:00:00`);
        for (let i = 0; i <= 14; i++) {
            const s = getDateInTimeZone(walk, laundryTimeZone);
            if (eligible(s)) { earliest = s; break; }
            walk = addDays(walk, 1);
        }
        if (dropoffDate !== earliest) {
            setDropoffDate(earliest);
        }
    }, [dryCleanOnly, maxTurnaroundHours, pickupDate, laundryTimeZone, dropoffDate, deliveryTimeSlots, allowedDeliveryDays, laundryData]);
    // Tenant offers Subscribe & Save when it has frequency options AND at least
    // one per-bag (per-item) service in its catalog.
    const tenantHasPerBag = laundryServices.some(
        (s) => s.inputWeight === false || s.inputWeight === 'false'
    );
    const subscribeSaveAvailable =
        (laundryFrequency && laundryFrequency.length > 0) && tenantHasPerBag;

    // Plan selection now happens on the Schedule page (no separate Order Type
    // step). This sets orderType + frequency WITHOUT clearing the cart or
    // changing the step. Subscribe & Save is only selectable for a bag-only cart
    // (rule C): if the cart isn't bag-only we fall back to recurring.
    const handleSelectPlan = (type, interval) => {
        planChosenRef.current = true;
        if (type === 'subscribe-save' && !(subscribeSaveAvailable && cartIsBagOnly)) {
            return; // guarded by UI, but never accept S&S for a non-bag cart
        }
        setOrderType(type);
        if (type === 'one-time') {
            setFrequency(null);
        } else if (interval) {
            setFrequency(interval);
        } else if (!frequency && laundryFrequency.length > 0) {
            // Recurring / Subscribe & Save: default the interval so the customer
            // can continue without an extra tap. Prefer Weekly when the tenant
            // offers it, else fall back to the first available interval.
            const weekly = laundryFrequency.find(
                (f) => String(f).toLowerCase().replace(/[-\s]/g, '') === 'weekly'
            );
            setFrequency(weekly || laundryFrequency[0]);
        }
    };

    // Safety net: if the cart stops being bag-only while Subscribe & Save is
    // selected (customer went back and added a per-pound item), demote to a
    // plain recurring order so the bag-only discount never applies to a mixed cart.
    useEffect(() => {
        if (orderType === 'subscribe-save' && !cartIsBagOnly) {
            setOrderType('frequency');
        }
    }, [orderType, cartIsBagOnly]);

    // Honor a plan pre-selected from the landing page (e.g. a "Recurring" or
    // "Subscribe & Save" CTA). The Order Type step is gone, so seed orderType
    // once on mount. Subscribe & Save is only seeded when the tenant offers it;
    // the bag-only rule is still enforced on the Schedule page + safety net.
    useEffect(() => {
        if (!preSelectedType) return;
        // Seed a default interval (prefer Weekly) so a deep-linked recurring /
        // Subscribe & Save order can proceed without a manual frequency pick.
        const seedFrequency = () => {
            if (frequency || laundryFrequency.length === 0) return;
            const weekly = laundryFrequency.find(
                (f) => String(f).toLowerCase().replace(/[-\s]/g, '') === 'weekly'
            );
            setFrequency(weekly || laundryFrequency[0]);
        };
        if (preSelectedType === 'frequency' && laundryFrequency.length > 0) {
            setOrderType('frequency');
            seedFrequency();
        } else if (preSelectedType === 'subscribe-save' && subscribeSaveAvailable) {
            setOrderType('subscribe-save');
            seedFrequency();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preSelectedType, laundryFrequency, subscribeSaveAvailable]);

    // Land on a weekly repeat when the shop offers one. Most people leave a
    // selected choice alone, and a weekly pickup is the one the shop wants.
    useEffect(() => {
        if (repeatSeededRef.current || planChosenRef.current) return;
        if (preSelectedType) return;
        if (searchParams.get('reorder')) return;
        if (!laundryFrequency || laundryFrequency.length === 0) return;
        repeatSeededRef.current = true;
        const weekly = laundryFrequency.find(
            (f) => String(f).toLowerCase().replace(/[-\s]/g, '') === 'weekly'
        );
        setOrderType('frequency');
        setFrequency(weekly || laundryFrequency[0]);
    }, [laundryFrequency, preSelectedType, searchParams]);

    // Accounts made with email only have no phone; drivers need one, so ask at
    // checkout. A ref, because the retried handlePlaceOrder closure still sees
    // the old `user`.
    const { user, updateUser } = useCustomerAuth();
    const phoneSavedRef = useRef(false);
    const [phonePromptOpen, setPhonePromptOpen] = useState(false);
    const [promptPhone, setPromptPhone] = useState('');
    const [isSavingPhone, setIsSavingPhone] = useState(false);

    const handleSavePhone = async () => {
        if (promptPhone.length !== 10) {
            toast({ title: "Invalid phone number", description: "Please enter a 10-digit US phone number.", status: "error", duration: 4000, isClosable: true });
            return;
        }
        setIsSavingPhone(true);
        try {
            const saved = await saveCustomerPhone(`+1${promptPhone}`);
            phoneSavedRef.current = true;
            updateUser({ phone: saved });
            setPhonePromptOpen(false);
            handlePlaceOrder();
        } catch (error) {
            const detail = error?.response?.data?.detail;
            toast({ title: "Couldn't save phone", description: typeof detail === 'string' ? detail : "Please try again.", status: "error", duration: 6000, isClosable: true });
        } finally {
            setIsSavingPhone(false);
        }
    };

    // Updated handlePlaceOrder using buildOrderPayload (Task 5.3)
    const handlePlaceOrder = async () => {
        if (!isPlacingForChild && !user?.phone && !phoneSavedRef.current) {
            setPhonePromptOpen(true);
            return false;
        }
        if (cart.items.length === 0) {
            toast({ title: "Error", description: "Cart is empty.", status: "error", duration: 3000, isClosable: true });
            return false;
        }
        // Serviceability backstop: never place an order without a validated,
        // serviceable address, even if the flow was reached out of order.
        if (!address || !isAddressValidated) {
            toast({ title: "Address needed", description: "Please enter and confirm a serviceable pickup address.", status: "error", duration: 4000, isClosable: true });
            setActiveStep(1); // Address step
            return false;
        }
        // Time windows are only required when the tenant lets customers choose
        // them. For date-only tenants (customerTimeSelectionEnabled = false) the
        // times may be blank; the operator assigns them and notifies the client.
        const timeSelectionOn = laundryData?.customerTimeSelectionEnabled ?? true;
        if (!pickupDate || !dropoffDate || (timeSelectionOn && (!pickupTime || !dropoffTime))) {
            toast({ title: "Error", description: "Please fill in all schedule fields.", status: "error", duration: 3000, isClosable: true });
            return false;
        }
        if (!customerPaymentId && !payByInvoice && !(laundryData?.giftCardsEnabled && (giftCardCode || '').trim())) {
            toast({ title: "Payment Information Missing", description: "Add a card or a gift card code.", status: "error", duration: 3000, isClosable: true });
            return false;
        }

        // Compute grand total (Phase 2c: include selected add-ons)
        const selectedAddonList = Object.values(selectedAddons);
        const billedWeight = getBilledWeight(cart.items);
        const addonsTotal = getAddonsTotal(selectedAddonList, billedWeight, cart.items);
        const subtotal = getCartSubtotal(cart.items) + addonsTotal;
        // Plan-aware discount: Subscribe & Save (per-bag) uses subscriptionDiscount;
        // the plain Recurring plan uses recurringDiscount; one-time gets none.
        // Mirrors the backend weigh-in recalc so the first order matches renewals.
        const planDiscount = orderType === 'subscribe-save'
            ? (laundryData?.subscriptionDiscount || 0)
            : orderType === 'frequency'
                ? (laundryData?.recurringDiscount || 0)
                : 0;
        const discountAmount = planDiscount > 0 ? subtotal * (planDiscount / 100) : 0;
        const taxableAmount = subtotal - discountAmount;
        const taxRate = laundryData?.taxRate || 0;
        const tax = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;
        // For a percentage tip, compute the dollar amount from the taxable
        // subtotal (there is no tip selector on the review screen that would
        // have computed it). Otherwise use the entered custom amount.
        const tipAmount = tip.tipType === 'percentage'
            ? Math.round(taxableAmount * ((parseFloat(tip.tipPercentage) || 0) / 100) * 100) / 100
            : (parseFloat(tip.tipAmount || '0') || 0);
        const grandTotal = taxableAmount + tax + tipAmount;

        const payload = buildOrderPayload(cart, {
            customerId,
            laundryId,
            address,
            doorNumber,
            addressInstructions,
            specialInstructions,
            pickupDate: formatISO(parseISO(pickupDate), { representation: 'date' }),
            pickupTimeInterval: pickupTime,
            dropoffDate: formatISO(parseISO(dropoffDate), { representation: 'date' }),
            dropoffTimeInterval: dropoffTime,
            dryCleanDropoffDate: mixedDryClean ? dryCleanDropoffDate : null,
            dryCleanDropoffTimeInterval: mixedDryClean ? dropoffTime : null,
            frequency: frequency || null,
            laundryBags,
            grandTotal: grandTotal.toFixed(2),
            subTotal: subtotal.toFixed(2),
            addons: selectedAddonList,
            saveAddonPrefs,
            tip: {
                tipAmount: tipAmount.toFixed(2),
                tipPercentage: tip.tipPercentage,
                tipType: tip.tipType,
                tipMethod: tip.tipMethod,
                tipReceiverId: tip.tipReceivedId,
            },
            coupon: promoCode,
            giftCardCode: laundryData?.giftCardsEnabled ? (giftCardCode || '').trim() : '',
            pickupService: pickupService || 'LaundryDriver',
            dropoffService: dropoffService || 'LaundryDriver',
            customerPaymentId,
            payByInvoice,
        });

        // If using Uber, change operation
        if (pickupService === 'Uber' || dropoffService === 'Uber') {
            payload.operation = 'uberPlaceOrder';
        }
        payload.uberPickupFrequency = uberPickupFrequency;
        payload.uberDropoffFrequency = uberDropoffFrequency;
        payload.doordashPickupFrequency = doordashPickupFrequency;
        payload.doordashDropoffFrequency = doordashDropoffFrequency;
        payload.saveSpecialInstructions = saveSpecialInstructions;
        payload.autoCharge = !!frequency;

        // Multi-address ("Airbnb cleaner"): attach the full property list as
        // segments. The primary address (already in the payload) is property 1;
        // the extra properties follow. Only sent when there are 2+ properties so
        // the backend treats it as multi-address (else it's a normal order).
        if (isMultiProperty) {
            const validExtras = extraProperties.filter(p => (p.address || '').trim());
            if (validExtras.length > 0) {
                payload.segments = [
                    { address, unitName: '', doorNumber, addressInstructions },
                    ...validExtras.map(p => ({
                        ...(p.addressId ? { addressId: p.addressId } : {}),
                        address: p.address.trim(),
                        unitName: (p.unitName || '').trim(),
                        doorNumber: p.doorNumber || '',
                        addressInstructions: p.addressInstructions || '',
                    })),
                ];
            }
        }

        // Commercial hierarchy: place this order on behalf of a child location.
        // The backend re-points the order owner to the child after confirming the
        // parent->child membership; a non-parent supplying these gains nothing.
        if (isPlacingForChild) {
            payload.actingCustomerId = customerId;
            payload.onBehalfOfCustomerId = forCustomerId;
        }

        try {
            setOrderProcessing(true);
            const response = await axios.post(
                `${process.env.REACT_APP_AWS_API_URL}/api/customer/place-order`,
                payload,
                { headers: { 'x-api-key': authToken } }
            );
            if (response.data.status === 'success') {
                track('purchase', {
                    transaction_id: response.data.orderId,
                    value: Number(grandTotal.toFixed(2)),
                    currency: 'USD',
                    order_plan: orderType,
                    frequency: frequency || undefined,
                    pricing_type: payload.pricingType,
                });
                toast({ title: "Order Confirmed", description: "Your order has been placed!", status: "success", duration: 3000, isClosable: true });
                setOrderProcessing(false);
                navigate(`/${laundryId}/user/order-success`);
                return true;
            } else {
                // Check if card was declined — send customer to Payment step to update card
                if (response.data.code === 'CARD_DECLINED') {
                    toast({ title: "Card Declined", description: response.data.message || "Please update your payment method.", status: "warning", duration: 5000, isClosable: true });
                    setIsPaymentStepValid(false);
                    setActiveStep(3); // Send back to Payment step
                } else if (response.data.code === 'PHONE_REQUIRED' && !isPlacingForChild) {
                    phoneSavedRef.current = false;
                    setPhonePromptOpen(true);
                } else {
                    toast({ title: "Order Failed", description: response.data.message || "Failed.", status: "error", duration: 3000, isClosable: true });
                }
                setOrderProcessing(false);
                return false;
            }
        } catch (error) {
            toast({ title: "Error", description: error.message || "Failed.", status: "error", duration: 3000, isClosable: true });
            setOrderProcessing(false);
            return false;
        }
    };

    // Function to validate the address
    const validateAddress = async (addr) => {
        try {
            setIsAddressValidating(true);
            const response = await axios.get(
                `${process.env.REACT_APP_AWS_API_URL}/api/laundry/validate-address`,
                {
                    params: {
                        operation: 'validateAddress',
                        laundryId: laundryId,
                        address: addr,
                        // Pass the signed-in customer so unserved/too-far attempts
                        // are recorded in Zip Demand with their email + phone.
                        customerId: customerId || undefined,
                    },
                    headers: {
                        'x-api-key': process.env.REACT_APP_AWS_API_KEY,
                    },
                }
            );
            const data = response.data;
            if (data.status === 'success') {
                if (data.serviceable) {
                    setIsAddressValidated(true);
                    setTooFar(null);
                    // Zone-restricted pickup days for this address (null when the
                    // tenant has no zones or the zip isn't in one — use global days).
                    setAllowedPickupDays(Array.isArray(data.allowedPickupDays) ? data.allowedPickupDays : null);
                    // Zone-specific delivery days for the return date (null when
                    // no zone / no match / zone has no delivery days -> global).
                    setAllowedDeliveryDays(Array.isArray(data.allowedDeliveryDays) ? data.allowedDeliveryDays : null);
                    if (!isPlacingForChild) localStorage.setItem('customerAddress', addr);
                    return true;
                } else if (data.reason === 'too_far') {
                    // Serviceable zip but beyond the shop's max delivery distance.
                    // Backend already captured it as demand. Show a PERSISTENT box
                    // (with a clickable, formatted phone) rather than a toast that
                    // vanishes before the customer can read/tap the number.
                    setTooFar({ contactPhone: data.contactPhone || '' });
                    setIsAddressValidated(false);
                    return false;
                } else {
                    toast({
                        title: "Address Not Serviceable",
                        description: "The entered address is not serviceable. Please enter a different address.",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                    setIsAddressValidated(false);
                    return false;
                }
            } else {
                toast({
                    title: "Error",
                    description: `Error: ${data.message}`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                setIsAddressValidated(false);
                return false;
            }
        } catch (error) {
            console.error('Error checking serviceability:', error);
            toast({
                title: "Error",
                description: 'An error occurred while checking the serviceability. Please try again.',
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            setIsAddressValidated(false);
            return false;
        }
        finally {
            setIsAddressValidating(false);
        }
    };

    // Address step Continue: validate serviceability, then advance to Schedule
    // (step 3) only when the address is confirmed serviceable. A cached-but-
    // already-validated address still re-validates here so a stale localStorage
    // entry can never carry an unserviceable address forward.
    const handleAddressContinue = async () => {
        if (!address) {
            toast({
                title: "Address required",
                description: "Please enter your pickup address to continue.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        const ok = await validateAddress(address);
        if (ok) {
            setActiveStep(2); // Schedule
        }
    };

    // Keyed by the property's stable id (not array index) so add/remove in the
    // middle of the list never updates the wrong row.
    const setExtraPropertyAddress = (key, address) => {
        setExtraProperties(prev => prev.map((x) => x._key === key ? { ...x, address } : x));
    };

    // Smart Defaults: advance step with payment skip logic.
    // Steps: 0 Services, 1 Address, 2 Schedule, 3 Payment, 4 Review.
    // Payment can be skipped when leaving Schedule (step 2).
    const advanceStep = (fromStep) => {
        if (fromStep === 2) {
            // Skip payment step ONLY when we're confident payment is covered:
            // 1. No Stripe configured → pay at pickup (always safe)
            // 2. Commercial account → pay by invoice (always safe)
            // 3. Reorder path (card was already validated on a prior order)
            // For normal flow with card on file, still go through payment step
            // so the $1 hold can verify the card is valid.
            const isReorder = !!searchParams.get('reorder');
            const canSkipPayment = !stripePromise || isCommercialCustomer || (!!customerPaymentId && isReorder);

            if (canSkipPayment) {
                setIsPaymentStepValid(true);
                if (!stripePromise && !customerPaymentId) {
                    setPayByInvoice(true);
                }
                setActiveStep(4); // Review
                return;
            }
        }
        setActiveStep(fromStep + 1);
    };

    // Quick Reorder: handle ?reorder=orderId query param
    useEffect(() => {
        const reorderOrderId = searchParams.get('reorder');
        if (!reorderOrderId || !servicesLoaded || !authToken) return;

        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/get-order-id-info`, {
            params: { operation: 'getCustomerOrderInfo', orderId: reorderOrderId, laundryId },
            headers: { 'x-api-key': authToken },
        }).then(res => {
            const order = res.data?.body?.data || res.data?.order || res.data;
            if (!order || !order.services) return;

            // Pre-fill cart from previous order
            dispatch({ type: 'CLEAR_CART' });
            (order.services || []).forEach(svc => {
                dispatch({
                    type: 'ADD_ITEM',
                    payload: {
                        serviceId: svc.serviceName,
                        serviceName: svc.serviceName,
                        categoryId: 'reorder',
                        categoryName: 'Reorder',
                        isDryClean: !!(laundryServices || []).find((s) => (s.serviceName || '').toLowerCase() === (svc.serviceName || '').toLowerCase())?.isDryClean,
                        price: parseFloat(svc.servicePrice || 0),
                        inputWeight: parseFloat(svc.weightOrCount || 0) > 0 && parseFloat(svc.weightOrCount) !== 1,
                        quantity: parseFloat(svc.weightOrCount || 1),
                    },
                });
            });

            // A reorder is a single order. Don't let the weekly default override it.
            planChosenRef.current = true;
            repeatSeededRef.current = true;
            setOrderType('one-time');
            setFrequency(null);

            // Auto-validate payment if possible
            if (customerPaymentId || !stripePromise) {
                setIsPaymentStepValid(true);
                if (!stripePromise) setPayByInvoice(true);
            }

            // Jump to Review (step 5) when we already have a validated address
            // (cached from a prior order); otherwise land on the Address step (1)
            // so serviceability is confirmed before the reorder can be placed.
            setActiveStep(isAddressValidated && address ? 4 : 1);
        }).catch(err => {
            console.error('Reorder fetch failed:', err);
            // Fall back to normal flow on error
        });
    }, [servicesLoaded, searchParams, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

    const themeGradient = (() => {
        const gradientMap = {
            blue: "linear-gradient(180deg, #EBF8FF 0%, #F7FAFC 100%)",
            green: "linear-gradient(180deg, #F0FFF4 0%, #F7FAFC 100%)",
            purple: "linear-gradient(180deg, #FAF5FF 0%, #F7FAFC 100%)",
            teal: "linear-gradient(180deg, #E6FFFA 0%, #F7FAFC 100%)",
            orange: "linear-gradient(180deg, #FFFAF0 0%, #F7FAFC 100%)",
            red: "linear-gradient(180deg, #FFF5F5 0%, #F7FAFC 100%)",
            pink: "linear-gradient(180deg, #FFF5F7 0%, #F7FAFC 100%)",
            cyan: "linear-gradient(180deg, #EDFDFD 0%, #F7FAFC 100%)",
        };
        return gradientMap[laundryData?.themeColor] || gradientMap.blue;
    })();

    if (groupCtx?.blocked) {
        return (
            <Box padding={[2,4,6]} bg={themeGradient} minHeight="100vh">
                <Stack spacing={4} maxWidth={["100%", "600px", "800px"]} margin="auto" px={[2, 4, 6]} py={[4, 6, 8]}>
                    <Alert status="warning" borderRadius="lg"><AlertIcon />{groupCtx.message}</Alert>
                    <Flex gap={3} flexWrap="wrap">
                        {(groupCtx.isParent || isPlacingForChild) && (
                            <Button colorScheme="blue" onClick={() => navigate(`/${laundryId}/commercial`)}>Go to Commercial account</Button>
                        )}
                        <Button variant="outline" onClick={() => navigate(`/${laundryId}/user/my-orders`)}>View my orders</Button>
                    </Flex>
                </Stack>
            </Box>
        );
    }

    if (groupCtx?.isParent && groupCtx.locations?.length > 0 && !isPlacingForChild && !ownAccountChosen) {
        return (
            <Box padding={[2,4,6]} bg={themeGradient} minHeight="100vh">
                <Stack spacing={4} maxWidth={["100%", "600px", "800px"]} margin="auto" px={[2, 4, 6]} py={[4, 6, 8]}>
                    <Box>
                        <Text fontSize="xl" fontWeight="700">Which location is this order for?</Text>
                        {groupCtx.groupName && <Text fontSize="sm" color="gray.600">{groupCtx.groupName}</Text>}
                    </Box>
                    {groupCtx.locations.map((loc) => (
                        <Button
                            key={loc.customerId}
                            onClick={() => chooseLocation(loc.customerId)}
                            isDisabled={loc.isSuspended}
                            variant="outline"
                            bg="white"
                            height="auto"
                            py={4}
                            px={4}
                            justifyContent="flex-start"
                            textAlign="left"
                            whiteSpace="normal"
                        >
                            <Box>
                                <Text fontWeight="600">{loc.locationLabel || loc.name || 'Location'}</Text>
                                {loc.address && <Text fontSize="sm" fontWeight="400" color="gray.600">{loc.address}</Text>}
                                {loc.isSuspended && <Text fontSize="xs" color="orange.600">Paused by the laundry</Text>}
                            </Box>
                        </Button>
                    ))}
                    <Button variant="ghost" colorScheme="blue" alignSelf="flex-start" onClick={() => chooseLocation(null)}>
                        For my own account instead
                    </Button>
                </Stack>
            </Box>
        );
    }

    return (
        <Box padding={[2,4,6]} bg={themeGradient} minHeight="100vh">
            <Stack spacing={[3,6,8]} maxWidth={["100%", "600px", "800px"]} margin="auto" px={[1, 4, 6]} py={[2, 6, 8]}>
                {(
                    // Order Flow with 6-step stepper (services-first; address is step 2)
                    <>
                        {groupCtx?.childLabel && (
                            <Flex justify="space-between" align="center" bg="blue.50" borderRadius="lg" px={4} py={3} border="1px solid" borderColor="blue.200" gap={3} flexWrap="wrap">
                                <Box>
                                    <Text fontSize="xs" color="blue.700">Ordering for</Text>
                                    <Text fontWeight="600" color="blue.900">{groupCtx.childLabel}{groupCtx.groupName ? ` · ${groupCtx.groupName}` : ''}</Text>
                                    <Text fontSize="xs" color="blue.700">Billed to your monthly commercial invoice.</Text>
                                </Box>
                                <Button size="xs" variant="ghost" colorScheme="blue" onClick={reopenLocationPicker}>
                                    Change location
                                </Button>
                            </Flex>
                        )}
                        {/* Show confirmed address with change option once validated */}
                        {address && isAddressValidated && (
                            <Flex justify="space-between" align="center" bg="white" borderRadius="lg" px={4} py={2} mb={[0, 3]} border="1px solid" borderColor="gray.100">
                                <Box>
                                    <Box fontSize="xs" color="gray.500">Pickup Address</Box>
                                    <Box fontSize="sm" fontWeight="500" color="gray.700" noOfLines={1}>{address}</Box>
                                </Box>
                                <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => { setIsAddressValidated(false); localStorage.removeItem('customerAddress'); setActiveStep(1); }}>
                                    Change
                                </Button>
                            </Flex>
                        )}
                        {laundryData?.laundryLogo && activeStep === 0 && (
                            <Box mb={[0, 2]} textAlign="center">
                                <Image src={laundryData.laundryLogo} alt={laundryData?.laundryName} maxH={{ base: '56px', md: '130px' }} objectFit="contain" mx="auto" />
                            </Box>
                        )}
                        {/* Phones: titles for all five steps don't fit, so show
                            indicators only plus the current step's name. */}
                        <Box>
                            <Stepper index={activeStep} size={{ base: 'sm', md: 'md' }} gap="0" colorScheme="blue">
                                {steps.map((step, index) => (
                                    <Step key={index}>
                                        <StepIndicator>
                                            <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                                        </StepIndicator>
                                        <StepTitle display={{ base: 'none', md: 'block' }} fontSize={['xs','sm','md']}>{step.title}</StepTitle>
                                        {index !== steps.length - 1 && <StepSeparator />}
                                    </Step>
                                ))}
                            </Stepper>
                            <Text display={{ base: 'block', md: 'none' }} mt={2} fontSize="sm" color="gray.600" textAlign="center">
                                Step {Math.min(activeStep, steps.length - 1) + 1} of {steps.length}: <Text as="span" fontWeight="700" color="gray.800">{steps[Math.min(activeStep, steps.length - 1)]?.title}</Text>
                            </Text>
                        </Box>

                        <Box bg="white" borderRadius="2xl" boxShadow="sm" border="1px solid" borderColor="gray.100" padding={[3,5,6]}>
                            {/* Services skeleton — shown while the catalog loads so the
                                Services step never flashes an empty white box. */}
                            {activeStep === 0 && !servicesLoaded && (
                                <VStack spacing={4} align="stretch" py={2}>
                                    <Skeleton height="20px" width="140px" borderRadius="md" />
                                    {[0, 1, 2, 3].map((i) => (
                                        <Box key={i} p={3} borderRadius="lg" border="1px solid" borderColor="gray.100">
                                            <Skeleton height="16px" width="60%" mb={2} borderRadius="md" />
                                            <SkeletonText noOfLines={1} skeletonHeight="3" width="40%" />
                                        </Box>
                                    ))}
                                </VStack>
                            )}

                            {/* Membership offer — shown early (Services step) so
                                the plan is discoverable while ordering, not just
                                at the final Review step. Non-members / tenants
                                with plans only; self-hides otherwise. */}
                            {activeStep === 0 && servicesLoaded && (
                                <MembershipOfferBanner
                                    laundryId={laundryId}
                                    customerId={customerId}
                                    subtotal={getCartSubtotal(cart.items) + getAddonsTotal(Object.values(selectedAddons), getBilledWeight(cart.items), cart.items)}
                                    estWeightLb={getBilledWeight(cart.items)}
                                />
                            )}

                            {/* Step 0: Unified Service Page (full catalog — no order-type filtering) */}
                            {activeStep === 0 && servicesLoaded && (
                                <UnifiedServicePage
                                    laundryServices={laundryServices}
                                    serviceCategories={serviceCategories}
                                    cart={cart}
                                    dispatch={dispatch}
                                    onContinue={() => setActiveStep(1)}
                                    themeColor={laundryData?.themeColor || 'blue'}
                                    contactPhone={contactPhone}
                                    laundryAddress={laundryAddress}
                                    bagSizeGuide={laundryData?.bagSizeGuide || []}
                                    bagSizeDisclaimer={laundryData?.bagSizeDisclaimer || ''}
                                    bagOverageRate={laundryData?.bagOverageRate ?? 1.95}
                                    addonsTotal={getAddonsTotal(Object.values(selectedAddons), getBilledWeight(cart.items), cart.items)}
                                    footerSlot={
                                        addonsEnabled && laundryAddons.length > 0 ? (
                                            <AddonPicker
                                                addons={laundryAddons}
                                                selected={selectedAddons}
                                                onChange={setSelectedAddons}
                                                billedWeight={getBilledWeight(cart.items)}
                                                saveAsDefault={saveAddonPrefs}
                                                onSaveAsDefaultChange={setSaveAddonPrefs}
                                            />
                                        ) : null
                                    }
                                />
                            )}

                            {/* Step 1: Address */}
                            {activeStep === 1 && (
                                <VStack spacing={5} align="stretch">
                                    <Box>
                                        <Heading size="md" color="gray.800" mb={1}>Where should we pick up?</Heading>
                                        <Text fontSize="sm" color="gray.500">Enter your pickup address so we can confirm we serve your area.</Text>
                                    </Box>
                                    <FormControl id="address" isRequired>
                                        <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                            Pickup Address
                                        </FormLabel>
                                        <PlacesSuggestInput
                                            placeholder="Enter your address"
                                            value={address}
                                            size="lg"
                                            onChange={(text) => { setAddress(text); if (isAddressValidated) setIsAddressValidated(false); if (tooFar) setTooFar(null); }}
                                            onSelect={({ formattedAddress }) => setAddress(formattedAddress)}
                                        />
                                    </FormControl>

                                    {/* Outside delivery range — serviceable zip but beyond max
                                        distance. Persistent + dismissible with a clickable phone. */}
                                    {tooFar && (
                                        <Box bg="orange.50" borderRadius="xl" p={4} border="1px solid" borderColor="orange.200">
                                            <Flex justify="space-between" align="start" mb={1}>
                                                <Text fontWeight="700" color="orange.700" fontSize="sm">
                                                    This address is outside our delivery range
                                                </Text>
                                                <Button variant="ghost" size="xs" color="orange.700" onClick={() => setTooFar(null)} aria-label="Dismiss">✕</Button>
                                            </Flex>
                                            <Text fontSize="xs" color="gray.600" mb={2}>
                                                This address is beyond our standard delivery range.
                                                {tooFar.contactPhone ? ' Please give us a call and we\'ll see what we can do.' : ' Please contact us and we\'ll see what we can do.'}
                                            </Text>
                                            {tooFar.contactPhone && (
                                                <Button as="a" href={telHref(tooFar.contactPhone)} size="sm" colorScheme="orange">
                                                    Call {formatPhone(tooFar.contactPhone)}
                                                </Button>
                                            )}
                                        </Box>
                                    )}

                                    <FormControl id="doorNumber">
                                        <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                            Apartment or Unit Number
                                        </FormLabel>
                                        <Input
                                            type="text"
                                            placeholder="Apt, Suite, Unit (optional)"
                                            value={doorNumber}
                                            onChange={(e) => setDoorNumber(e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl id="addressInstructions">
                                        <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                            Delivery Instructions
                                        </FormLabel>
                                        <Input
                                            type="text"
                                            placeholder="e.g. Leave at front door"
                                            value={addressInstructions}
                                            onChange={(e) => setAddressInstructions(e.target.value)}
                                        />
                                    </FormControl>

                                    {/* Multi-property ("Airbnb cleaner") accounts: add more
                                        properties on this order. Each is weighed and billed
                                        separately; the final total is based on the measured
                                        weight per property. */}
                                    {isMultiProperty && (
                                        <Box bg="blue.50" borderRadius="xl" p={4} border="1px solid" borderColor="blue.200">
                                            <Text fontWeight="700" color="blue.700" fontSize="sm" mb={1}>
                                                🏠 Additional properties
                                            </Text>
                                            <Text fontSize="xs" color="gray.600" mb={3}>
                                                Add every property this order covers. We'll weigh each one
                                                separately and itemize your invoice by property.
                                            </Text>
                                            <VStack spacing={3} align="stretch">
                                                {extraProperties.map((p, i) => (
                                                    <Box key={p._key} bg="white" borderRadius="md" p={3} border="1px solid" borderColor="gray.200">
                                                        <Flex justify="space-between" align="center" mb={2}>
                                                            <Text fontSize="xs" fontWeight="600" color="gray.600">Property {i + 2}</Text>
                                                            <Button size="xs" variant="ghost" colorScheme="red"
                                                                onClick={() => setExtraProperties(prev => prev.filter((x) => x._key !== p._key))}>
                                                                Remove
                                                            </Button>
                                                        </Flex>
                                                        <Box mb={2}>
                                                            <PlacesSuggestInput size="sm" placeholder="Property address"
                                                                value={p.address}
                                                                onChange={(text) => setExtraPropertyAddress(p._key, text)}
                                                                onSelect={({ formattedAddress }) => setExtraPropertyAddress(p._key, formattedAddress)}
                                                            />
                                                        </Box>
                                                        <Input size="sm" mb={2} placeholder="Nickname (e.g. Beach House) — optional"
                                                            value={p.unitName}
                                                            onChange={(e) => setExtraProperties(prev => prev.map((x) => x._key === p._key ? { ...x, unitName: e.target.value } : x))}
                                                        />
                                                        <Input size="sm" placeholder="Unit / instructions (optional)"
                                                            value={p.addressInstructions}
                                                            onChange={(e) => setExtraProperties(prev => prev.map((x) => x._key === p._key ? { ...x, addressInstructions: e.target.value } : x))}
                                                        />
                                                    </Box>
                                                ))}
                                                <Button size="sm" variant="outline" colorScheme="blue"
                                                    onClick={() => setExtraProperties(prev => [...prev, { _key: `prop_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, address: '', unitName: '', doorNumber: '', addressInstructions: '' }])}>
                                                    + Add property
                                                </Button>
                                            </VStack>
                                        </Box>
                                    )}

                                    <Button
                                        colorScheme="blue"
                                        size="lg"
                                        borderRadius="xl"
                                        w="100%"
                                        isLoading={isAddressValidating}
                                        loadingText="Checking your area..."
                                        boxShadow="md"
                                        onClick={handleAddressContinue}
                                    >
                                        Continue
                                    </Button>
                                </VStack>
                            )}

                            {/* Step 2: Schedule Page (plan + frequency + schedule) */}
                            {activeStep === 2 && (
                                <SchedulePage
                                    orderType={orderType}
                                    setOrderType={handleSelectPlan}
                                    subscribeSaveAvailable={subscribeSaveAvailable}
                                    cartIsBagOnly={cartIsBagOnly}
                                    minTurnaroundHours={maxTurnaroundHours}
                                    earliestDropoffDate={dryCleanOnly ? dryCleanMinDate : ""}
                                    dropoffDateLabel={dryCleanOnly ? "Dry cleaning back" : (mixedDryClean ? "Wash and fold back" : "Drop-off Date")}
                                    dryCleanDropoffDate={mixedDryClean ? dryCleanDropoffDate : ""}
                                    setDryCleanDropoffDate={setDryCleanDropoffDate}
                                    dryCleanMinDate={mixedDryClean ? dryCleanMinDate : ""}
                                    sameDayCutoffHours={laundryData?.sameDayCutoffHours ?? 4}
                                    customerTimeSelectionEnabled={laundryData?.customerTimeSelectionEnabled ?? true}
                                    closureDates={laundryData?.closureDates || []}
                                    allowedPickupDays={allowedPickupDays}
                                    allowedDeliveryDays={allowedDeliveryDays}
                                    subscriptionDiscount={laundryData?.subscriptionDiscount || 0}
                                    recurringDiscount={laundryData?.recurringDiscount || 0}
                                    pickupDate={pickupDate} setPickupDate={setPickupDate}
                                    pickupTime={pickupTime} setPickupTime={setPickupTime}
                                    dropoffDate={dropoffDate} setDropoffDate={setDropoffDate}
                                    dropoffTime={dropoffTime} setDropoffTime={setDropoffTime}
                                    pickupService={pickupService} setPickupService={setPickupService}
                                    dropoffService={dropoffService} setDropoffService={setDropoffService}
                                    frequency={frequency} setFrequency={setFrequency}
                                    promoCode={promoCode} setPromoCode={setPromoCode}
                                    specialInstructions={specialInstructions} setSpecialInstructions={setSpecialInstructions}
                                    setSaveSpecialInstructions={setSaveSpecialInstructions}
                                    laundryBags={laundryBags} setLaundryBags={setLaundryBags}
                                    deliveryTimeSlots={deliveryTimeSlots}
                                    deliveryTimeInterval={deliveryTimeInterval}
                                    laundryTimeZone={laundryTimeZone}
                                    laundryFrequency={laundryFrequency}
                                    frequencyPromotions={frequencyPromotions}
                                    promoDescriptionMessage={promoDescriptionMessage}
                                    setPromoDescriptionMessage={setPromoDescriptionMessage}
                                    uberEnv={uberEnv}
                                    uberExists={uberExists} setUberExists={setUberExists}
                                    laundryAddress={laundryAddress}
                                    address={address}
                                    uberPickupFrequency={uberPickupFrequency} setUberPickupFrequency={setUberPickupFrequency}
                                    uberDropoffFrequency={uberDropoffFrequency} setUberDropoffFrequency={setUberDropoffFrequency}
                                    doordashExists={doordashExists}
                                    missedPickupFee={missedPickupFee}
                                    courierHours={courierHours}
                                    doordashPickupFrequency={doordashPickupFrequency} setDoordashPickupFrequency={setDoordashPickupFrequency}
                                    doordashDropoffFrequency={doordashDropoffFrequency} setDoordashDropoffFrequency={setDoordashDropoffFrequency}
                                    laundryId={laundryId}
                                    customerId={customerId}
                                    onContinue={() => advanceStep(2)}
                                    onBack={() => setActiveStep(1)}
                                />
                            )}

                            {/* Step 3: Payment */}
                            {activeStep === 3 && isCommercialCustomer && (
                                <Box p={6} textAlign="center">
                                    <Text fontSize="lg" fontWeight="bold" mb={2}>💼 Commercial Account</Text>
                                    <Text color="gray.600" mb={4}>
                                        Your order will be invoiced. No card payment required.
                                    </Text>
                                    <Badge colorScheme="purple" fontSize="md" px={3} py={1} mb={4}>Pay by Invoice</Badge>
                                    <Button colorScheme="blue" onClick={() => { setActiveStep(4); }} w="100%">
                                        Continue to Review
                                    </Button>
                                </Box>
                            )}
                            {activeStep === 3 && !isCommercialCustomer && stripePromise && (
                                <Elements stripe={stripePromise}>
                                    <PaymentPage
                                        customerId={customerId}
                                        laundryId={laundryId}
                                        customerPaymentId={customerPaymentId}
                                        setCustomerPaymentId={setCustomerPaymentId}
                                        setIsPaymentStepValid={setIsPaymentStepValid}
                                        isPaymentStepValid={isPaymentStepValid}
                                        existingPaymentMethods={existingPaymentMethods}
                                        setExistingPaymentMethods={setExistingPaymentMethods}
                                        handleNextStep={() => setActiveStep(4)}
                                        payByInvoice={payByInvoice}
                                        setPayByInvoice={setPayByInvoice}
                                        isCommercial={isCommercialCustomer}
                                        giftCardCode={giftCardCode}
                                        setGiftCardCode={setGiftCardCode}
                                        giftCardsEnabled={!!laundryData?.giftCardsEnabled}
                                    />
                                </Elements>
                            )}
            {/* No Stripe keys configured for this tenant → online card capture
                                isn't possible, so the order is billed by invoice / paid at
                                pickup. We flag payByInvoice=true so the order is created as
                                an invoice order (no card, no $1 hold) rather than expecting
                                a card that can't be entered. Shown to every customer in this
                                case (not just commercial), since it's the only way to check
                                out when the tenant hasn't connected Stripe. */}
                            {activeStep === 3 && !isCommercialCustomer && !stripePromise && (
                                <Box p={6} textAlign="center">
                                    <Text fontSize="lg" fontWeight="bold" mb={2}>
                                        {laundryData?.giftCardsEnabled ? 'Pay at pickup, or use a gift card' : 'Pay at pickup'}
                                    </Text>
                                    <Text color="gray.600" mb={4}>
                                        Card payments aren't set up for this location yet.
                                        {laundryData?.giftCardsEnabled
                                            ? ' Enter a gift card code to pay now, or continue and pay when your laundry is picked up.'
                                            : ' Continue and pay when your laundry is picked up.'}
                                    </Text>
                                    {laundryData?.giftCardsEnabled && (
                                    <Input
                                        mb={4}
                                        value={giftCardCode}
                                        onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                                        placeholder="GIFT-XXXX-XXXX"
                                        bg="white"
                                    />
                                    )}
                                    <Button colorScheme="blue" onClick={() => {
                                        setIsPaymentStepValid(true);
                                        if (!(laundryData?.giftCardsEnabled && (giftCardCode || '').trim())) setPayByInvoice(true);
                                        setActiveStep(4);
                                    }}>
                                        Continue to Review
                                    </Button>
                                </Box>
                            )}

                            {/* Step 4: Review Order */}
                            {activeStep === 4 && (
                                <UnifiedReviewPage
                                    cart={cart}
                                    dispatch={dispatch}
                                    laundryId={laundryId}
                                    customerId={customerId}
                                    address={address}
                                    dropoffService={dropoffService}
                                    pickupDate={pickupDate}
                                    pickupTime={pickupTime}
                                    dropoffDate={dropoffDate}
                                    dropoffTime={dropoffTime}
                                    dryCleanDropoffDate={mixedDryClean ? dryCleanDropoffDate : ""}
                                    dropoffDateLabel={dryCleanOnly ? "Dry cleaning back" : (mixedDryClean ? "Wash and fold back" : "Dropoff")}
                                    customerTimeSelectionEnabled={laundryData?.customerTimeSelectionEnabled ?? true}
                                    tip={tip}
                                    setTip={setTip}
                                    taxRate={laundryData?.taxRate || 0}
                                    promoCode={promoCode}
                                    giftCardCode={giftCardCode}
                                    promoDescriptionMessage={promoDescriptionMessage}
                                    frequency={frequency}
                                    subscriptionDiscount={orderType === 'subscribe-save'
                                        ? (laundryData?.subscriptionDiscount || 0)
                                        : orderType === 'frequency'
                                            ? (laundryData?.recurringDiscount || 0)
                                            : 0}
                                    selectedAddons={Object.values(selectedAddons)}
                                    onPlaceOrder={handlePlaceOrder}
                                    onEdit={() => setActiveStep(0)}
                                    orderProcessing={orderProcessing}
                                />
                            )}
                        </Box>

                        {/* Services has nothing to go back to and Schedule has its own Back button. */}
                        {activeStep !== 0 && activeStep !== 2 && (
                            <Flex justify="space-between" mt={[0, 4]}>
                                <Button
                                    onClick={() => setActiveStep(activeStep - 1)}
                                    variant="ghost"
                                    colorScheme="gray"
                                >
                                    Previous
                                </Button>
                            </Flex>
                        )}
                    </>
                )}
            </Stack>

            <Modal isOpen={phonePromptOpen} onClose={() => setPhonePromptOpen(false)} isCentered>
                <ModalOverlay />
                <ModalContent mx={4}>
                    <ModalHeader>Add a phone number for your driver</ModalHeader>
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600" mb={3}>
                            The driver calls this number if they can't find you. We won't text it unless you turn on text updates in your account.
                        </Text>
                        <InputGroup size="lg">
                            <InputLeftAddon>+1</InputLeftAddon>
                            <Input
                                type="tel"
                                placeholder="(512) 555-0123"
                                value={promptPhone}
                                onChange={(e) => setPromptPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                onKeyPress={(e) => e.key === 'Enter' && handleSavePhone()}
                                data-testid="checkout-phone"
                            />
                        </InputGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setPhonePromptOpen(false)}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSavePhone} isLoading={isSavingPhone}>
                            Save and place order
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
